import { useState } from 'react';
import { Header, Card, Button, Badge, StatCard } from '../../shared/components';
import { AccessAdminTab } from './components/AccessAdminTab';
import { OfficeNetworksTab } from './components/OfficeNetworksTab';
import { ParametersTab } from './components/ParametersTab';
import { LabelsTab } from './components/LabelsTab';
import { HomeLocationsTab } from './components/HomeLocationsTab';
import { ReadingsAdminTab } from './components/ReadingsAdminTab';
import { ModalityCatalogTab } from './components/ModalityCatalogTab';
import { ScheduleExceptionsTab } from './components/ScheduleExceptionsTab';
import { RecordsAdminTab } from './components/RecordsAdminTab';
import { ShiftClosureHistoryTab } from './components/ShiftClosureHistoryTab';
import { InconsistenciesTab } from './components/InconsistenciesTab';
import { HolidaysTab } from './components/HolidaysTab';
import { RateFactorsTab } from './components/RateFactorsTab';
import { EmployeesTab } from './components/EmployeesTab';
import { ThemeSwitcher } from '../../shared/theme/ThemeSwitcher';
import { useCurrentUser, useSignOut } from '../../shared/auth/AuthProvider';
import { MapPinIcon, SettingsIcon, DatabaseIcon, UsersIcon, TimerIcon, AuditIcon } from '../../shared/icons';

const ROLE_LABEL: Record<string, string> = { admin: 'Administrador', supervisor: 'Supervisor', operator: 'Operador' };

type RootView = 'root' | 'redes' | 'casas' | 'lecturas' | 'parametros' | 'modalidades' | 'acceso' | 'registros' | 'excepciones' | 'inconsistencias' | 'empleados' | 'festivos' | 'factores' | 'cierres';

/**
 * Configuracion de TurnoGeo (solo Admin) - mismo patron de navegacion en dos
 * niveles que HrSettingsPage (Gestion Humana): tarjetas en la raiz, cada una
 * abre su propia pestana con Header + boton "Volver".
 *
 * "Acceso y usuarios" usa AccessAdminTab (propio de TurnoGeo: solo lista a sus miembros) para
 * otorgar/revocar el Rol de un colaborador EN TurnoGeo puntualmente, igual
 * que hacen Control Operativo y Gestion Humana. "Usuarios" (esta pagina) es
 * distinto: identidad documental + horario semanal, propio de TurnoGeo.
 */
export function TurnoGeoSettingsPage() {
  const isAdmin = useCurrentUser().role === 'admin';
  const [view, setView] = useState<RootView>('root');
  const [paramTab, setParamTab] = useState<'parametros' | 'etiquetas'>('parametros');

  if (view === 'redes') {
    return (
      <div>
        <Header title="Redes de oficina" subtitle="Configuración de TurnoGeo" onBack={() => setView('root')} />
        <div className="p-4"><OfficeNetworksTab /></div>
      </div>
    );
  }
  if (view === 'casas') {
    return (
      <div>
        <Header title="Ubicaciones registradas (casa y oficina)" subtitle="Configuración de TurnoGeo" onBack={() => setView('root')} />
        <div className="p-4"><HomeLocationsTab /></div>
      </div>
    );
  }
  if (view === 'lecturas') {
    return (
      <div>
        <Header title="Lecturas del Registrador" subtitle="Configuración de TurnoGeo" onBack={() => setView('root')} />
        <div className="p-4"><ReadingsAdminTab /></div>
      </div>
    );
  }
  if (view === 'parametros') {
    return (
      <div>
        <Header title="Parámetros y etiquetas" subtitle="Configuración de TurnoGeo" onBack={() => setView('root')} />
        <div className="space-y-4 p-4">
          <div className="flex gap-2">
            <Button variant={paramTab === 'parametros' ? 'primary' : 'outline'} onClick={() => setParamTab('parametros')}>Parámetros</Button>
            <Button variant={paramTab === 'etiquetas' ? 'primary' : 'outline'} onClick={() => setParamTab('etiquetas')}>Etiquetas</Button>
          </div>
          {paramTab === 'parametros' ? <ParametersTab /> : <LabelsTab />}
        </div>
      </div>
    );
  }
  if (view === 'modalidades') {
    return (
      <div>
        <Header title="Catálogo de modalidad" subtitle="Configuración de TurnoGeo" onBack={() => setView('root')} />
        <div className="p-4"><ModalityCatalogTab /></div>
      </div>
    );
  }
  if (view === 'registros') {
    return (
      <div>
        <Header title="Registros de marcación" subtitle="Configuración de TurnoGeo" onBack={() => setView('root')} />
        <div className="p-4"><RecordsAdminTab /></div>
      </div>
    );
  }
  if (view === 'cierres') {
    return (
      <div>
        <Header title="Turnos sin cerrar" subtitle="Histórico de cierres tardíos" onBack={() => setView('root')} />
        <div className="p-4"><ShiftClosureHistoryTab /></div>
      </div>
    );
  }
  if (view === 'inconsistencias') {
    return (
      <div>
        <Header title="Inconsistencias por auditar" subtitle="Configuración de TurnoGeo" onBack={() => setView('root')} />
        <div className="p-4"><InconsistenciesTab /></div>
      </div>
    );
  }
  if (view === 'empleados') {
    return (
      <div>
        <Header title="Empleados" subtitle="Carga masiva desde Excel" onBack={() => setView('root')} />
        <div className="p-4"><EmployeesTab /></div>
      </div>
    );
  }
  if (view === 'festivos') {
    return (
      <div>
        <Header title="Festivos" subtitle="Configuración de TurnoGeo" onBack={() => setView('root')} />
        <div className="p-4"><HolidaysTab /></div>
      </div>
    );
  }
  if (view === 'factores') {
    return (
      <div>
        <Header title="Factores de recargo (ley)" subtitle="Configuración de TurnoGeo" onBack={() => setView('root')} />
        <div className="p-4"><RateFactorsTab /></div>
      </div>
    );
  }
  if (view === 'excepciones') {
    return (
      <div>
        <Header title="Excepciones de horario" subtitle="Configuración de TurnoGeo" onBack={() => setView('root')} />
        <div className="p-4"><ScheduleExceptionsTab /></div>
      </div>
    );
  }
  if (view === 'acceso') {
    return (
      <div>
        <Header title="Acceso y usuarios" subtitle="Configuración de TurnoGeo" onBack={() => setView('root')} />
        <div className="p-4"><AccessAdminTab /></div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Configuración" subtitle="TurnoGeo" />
      <div className="space-y-6 p-4">
        <SessionCard />
        <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2">
          <StatCard icon={<MapPinIcon />} label="Sedes autorizadas (IP / GPS)" value="Redes de oficina" tone="brand" onClick={() => setView('redes')} />
          <StatCard icon={<MapPinIcon />} label="Casa y oficina que registra cada empleado" value="Ubicaciones registradas" tone="brand" onClick={() => setView('casas')} />
          <StatCard icon={<DatabaseIcon />} label="Lecturas guardadas por el personal" value="Lecturas (Registrador)" tone="info" onClick={() => setView('lecturas')} />
          <StatCard icon={<AuditIcon />} label="Marcaciones, correcciones y turnos abiertos" value="Registros de marcación" tone="brand" onClick={() => setView('registros')} />
          <StatCard icon={<AuditIcon />} label="Quién dejó el turno abierto, hora correcta y nota" value="Turnos sin cerrar" tone="warning" onClick={() => setView('cierres')} />
          <StatCard icon={<AuditIcon />} label="Almuerzos largos, toques, relojes alterados, turnos sin cierre" value="Inconsistencias por auditar" tone="brand" onClick={() => setView('inconsistencias')} />
          {isAdmin && <StatCard icon={<UsersIcon />} label="Carga masiva desde Excel, cuentas y ficha laboral" value="Empleados" tone="brand" onClick={() => setView('empleados')} />}
          <StatCard icon={<TimerIcon />} label="Horarios que se salen del general" value="Excepciones de horario" tone="brand" onClick={() => setView('excepciones')} />
          <StatCard icon={<TimerIcon />} label="Calendario que separa horas ordinarias de festivas" value="Festivos" tone="info" onClick={() => setView('festivos')} />
          {isAdmin && <StatCard icon={<SettingsIcon />} label="Noche 19:00, 35 %, extras, dominical 90 %, divisor" value="Factores de recargo" tone="info" onClick={() => setView('factores')} />}
          <StatCard icon={<SettingsIcon />} label="Jornada, almuerzo, foto, radios, etc." value="Parámetros" tone="info" onClick={() => setView('parametros')} />
          <StatCard icon={<DatabaseIcon />} label="Presencial / Remoto / Fuera de rango" value="Catálogo de modalidad" tone="info" onClick={() => setView('modalidades')} />
          {isAdmin && <StatCard icon={<UsersIcon />} label="Crear usuarios, roles, claves y fichas del personal" value="Acceso y usuarios" tone="info" onClick={() => setView('acceso')} />}
        </div>
      </div>
    </div>
  );
}

function SessionCard() {
  const user = useCurrentUser();
  const signOut = useSignOut();

  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-ink-800">{user.fullName}</p>
          <div className="mt-1">
            <Badge tone="info">{ROLE_LABEL[user.role] ?? user.role} · TurnoGeo</Badge>
          </div>
        </div>
        {signOut && (
          <Button variant="outline" size="md" onClick={signOut}>
            Cerrar sesión
          </Button>
        )}
      </div>
      <div className="mt-4 border-t border-ink-100 pt-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">Tema visual</p>
        <ThemeSwitcher />
      </div>
    </Card>
  );
}
